import { CheckOutlined, CloseOutlined, SearchOutlined, ToolOutlined } from '@ant-design/icons';
import { DrawerCloseButton, ServiceTag } from '@components';
import { useView } from '@hooks';
import { useSelectServices } from '@hooks';
import { L } from '@lib';
import { Service } from '@services';
import { Utils } from '@utils';
import { Button, Drawer, Empty, Input, Space, Spin, Typography } from 'antd';
import React, { useEffect, useMemo, useRef } from 'react';

const { Text } = Typography;

interface IProps {
  open: boolean;
  mode: 'single' | 'multiple';
  selectedIds: number[];
  selectedLabels?: Record<number, string>;
  cleanName?: boolean;
  fixedOnly?: boolean;
  showMyServicesOnly?: boolean;
  title?: string;
  onClose: () => void;
  onSelect: (service: Service) => void;
  onDeselect: (id: number) => void;
  onClear?: () => void;
}

export const ServicePickerDrawer: React.FC<IProps> = ({
  open,
  mode,
  selectedIds,
  selectedLabels,
  cleanName,
  fixedOnly,
  showMyServicesOnly,
  title,
  onClose,
  onSelect,
  onDeselect,
  onClear,
}) => {
  const { isMobile } = useView();
  const { services, loading, hasMore, handleSearch, loadMore } = useSelectServices({
    enabled: open,
    fixedOnly,
    showMyServicesOnly,
  });

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<any>(null);

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => searchInputRef.current?.focus?.(), 150);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!sentinelRef.current || !listRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      { root: listRef.current, rootMargin: '120px', threshold: 0 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [open, hasMore, loading, loadMore]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const getLabel = (service: Service) => Utils.formatServiceLabel(service, cleanName);

  const handleItemClick = (service: Service) => {
    if (mode === 'single') {
      onSelect(service);
      onClose();
      return;
    }
    if (selectedSet.has(service.id)) {
      onDeselect(service.id);
    } else {
      onSelect(service);
    }
  };

  const handleChipClose = (id: number) => {
    onDeselect(id);
  };

  const handleClearAll = () => {
    onClear?.();
  };

  const renderSelectedChips = () => {
    if (mode !== 'multiple' || selectedIds.length === 0) return null;
    return (
      <div
        style={{
          padding: '8px 16px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          alignItems: 'center',
        }}
      >
        {selectedIds.map((id) => (
          <ServiceTag
            key={id}
            variant="neutral"
            label={selectedLabels?.[id] ?? `#${id}`}
            closable
            onClose={(e) => {
              e.preventDefault();
              handleChipClose(id);
            }}
          />
        ))}
        {onClear && (
          <Button type="link" size="small" onClick={handleClearAll} style={{ marginLeft: 'auto' }}>
            {L('Clear all')}
          </Button>
        )}
      </div>
    );
  };

  const renderListItem = (service: Service) => {
    const isSelected = selectedSet.has(service.id);
    return (
      <div
        key={service.id}
        onClick={() => handleItemClick(service)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          cursor: 'pointer',
          background: isSelected ? '#e6f4ff' : 'transparent',
          borderBottom: '1px solid #f5f5f5',
          transition: 'background 0.15s',
        }}
        onMouseEnter={(e) => {
          if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = '#fafafa';
        }}
        onMouseLeave={(e) => {
          if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
        }}
      >
        <ToolOutlined style={{ color: isSelected ? '#1677ff' : '#8c8c8c', fontSize: 16 }} />
        {/* The list is where the full name is read before picking, so it wraps instead of clipping. */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontWeight: 500, color: '#333', overflowWrap: 'anywhere' }}>
            {getLabel(service)}
          </Text>
        </div>
        {isSelected && <CheckOutlined style={{ color: '#1677ff' }} />}
      </div>
    );
  };

  return (
    <Drawer
      title={title ?? L('Service select')}
      open={open}
      onClose={onClose}
      placement="right"
      width={isMobile ? '100%' : 480}
      destroyOnHidden
      closable={false}
      extra={<DrawerCloseButton icon={<CloseOutlined />} onClick={onClose} />}
      styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' } }}
      footer={
        mode === 'multiple' ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {selectedIds.length} {L('selected')}
            </Text>
            <Space>
              <Button onClick={onClose}>{L('Cancel')}</Button>
              <Button type="primary" onClick={onClose}>
                {L('Done')}
              </Button>
            </Space>
          </div>
        ) : null
      }
    >
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <Input
          ref={searchInputRef}
          allowClear
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          placeholder={L('Search services')}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      {renderSelectedChips()}

      <div
        ref={listRef}
        style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}
      >
        {services.map(renderListItem)}

        {services.length === 0 && !loading && (
          <div style={{ padding: 32 }}>
            <Empty description={L('No services found')} />
          </div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: 12 }}>
            <Spin size="small" />
          </div>
        )}

        <div ref={sentinelRef} style={{ height: 1 }} />
      </div>
    </Drawer>
  );
};
